// src/components/NavigationBar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listenNotifications, markAllNotificationsRead, listenUnreadMessages } from '../services/firestoreService';
import './NavigationBar.css';

const timeAgo = (ts) => {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60)  return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const NavigationBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [notifications, setNotifications] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const bellRef       = useRef(null);
  const mobileBellRef = useRef(null);

  React.useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Subscribe to real-time notifications when logged in
  useEffect(() => {
    if (!user) { setNotifications([]); return; }
    const unsub = listenNotifications(user.uid, setNotifications);
    return unsub;
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to unread message count
  useEffect(() => {
    if (!user) { setUnreadMessages(0); return; }
    const unsub = listenUnreadMessages(user.uid, setUnreadMessages);
    return unsub;
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close bell dropdown when clicking outside (checks both desktop and mobile bell refs)
  useEffect(() => {
    const handler = (e) => {
      const inDesktop = bellRef.current       && bellRef.current.contains(e.target);
      const inMobile  = mobileBellRef.current && mobileBellRef.current.contains(e.target);
      if (!inDesktop && !inMobile) setBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const handleBellOpen = () => {
    const next = !bellOpen;
    setBellOpen(next);
    if (next && unreadCount > 0) {
      markAllNotificationsRead(user.uid);
    }
  };

  const handleNotificationClick = (n) => {
    console.log('[NotificationClick] type:', n.type, '| jobId:', n.jobId, '| user.type:', user?.type);
    setBellOpen(false);
    if (user?.type === 'provider') {
      const YARD_TYPES = ['outbid', 'rival_job', 'rival_passed', 'rival_overtake', 'yard_fastest', 'yard_weekly', 'league_win', 'streak_bonus'];
      if (n.type === 'bid_accepted') {
        navigate('/provider-dashboard?tab=active');
      } else if (n.type === 'new_job') {
        navigate('/provider-dashboard?tab=jobs');
      } else if (n.type === 'referral_received') {
        // Navigate to Open Jobs and highlight the specific recommended job
        const url = n.jobId
          ? `/provider-dashboard?tab=jobs&highlightJobId=${n.jobId}`
          : '/provider-dashboard?tab=jobs';
        navigate(url);
      } else if (n.type === 'speed_bonus_reversed') {
        navigate('/provider-dashboard?tab=mybids');
      } else if (n.type === 'carrier_verified' || n.type === 'carrier_unverified') {
        navigate('/provider-dashboard');
      } else if (YARD_TYPES.includes(n.type)) {
        navigate('/provider-dashboard?tab=yard');
      } else if (n.jobId) {
        // Generic fallback: any notification with a jobId but no specific handler
        // goes to Open Jobs (more useful default than Active Jobs)
        navigate('/provider-dashboard?tab=jobs');
      } else {
        navigate('/provider-dashboard');
      }
    } else {
      // Customer notifications
      if (n.type === 'new_bid') {
        navigate('/customer-dashboard');
      } else {
        navigate('/customer-dashboard');
      }
    }
  };

  const handleMessageAlertClick = () => {
    setBellOpen(false);
    navigate(user?.type === 'provider' ? '/provider-dashboard?tab=active' : '/customer-dashboard');
  };

  const isActive = (path) => location.pathname === path ? 'active' : '';

  const unreadCount    = notifications.filter(n => !n.read).length;
  const hasOutbid      = notifications.some(n => !n.read && n.type === 'outbid');
  const totalBadge     = unreadCount + unreadMessages;

  return (
    <nav className="navbar">
      <Link to="/" className="navbar-logo">
        <span className="navbar-logo-the">THE</span>
        <span className="navbar-logo-main">WASTE<span className="navbar-logo-game">GAME</span></span>
      </Link>

      <div className={`navbar-links ${menuOpen ? 'open' : ''}`}>
        <Link to="/" className={`nav-link ${isActive('/')}`} onClick={() => setMenuOpen(false)}>Home</Link>
        <Link to="/services" className={`nav-link ${isActive('/services')}`} onClick={() => setMenuOpen(false)}>Services</Link>
        <Link to="/areas" className={`nav-link ${location.pathname === '/areas' || location.pathname.startsWith('/areas/') ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>Areas</Link>
        <Link to="/guides" className={`nav-link ${location.pathname === '/guides' || location.pathname.startsWith('/guides/') ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>Guides</Link>
        {user && (
          <Link
            to={user.type === 'customer' ? '/customer-dashboard' : '/provider-dashboard'}
            className={`nav-link ${isActive('/customer-dashboard') || isActive('/provider-dashboard')}`}
            onClick={() => setMenuOpen(false)}
          >
            Dashboard
          </Link>
        )}
        {!user ? (
          <>
            <Link to="/login" className={`nav-link ${isActive('/login')}`} onClick={() => setMenuOpen(false)}>Login</Link>
            <Link to="/signup" className="btn btn-primary btn-sm" onClick={() => setMenuOpen(false)}>Get Started</Link>
          </>
        ) : (
          <div className="nav-user">
            <span className="nav-user-name">{user.name}</span>
            {user.type === 'provider' && (
              <span className="nav-credits">♻️ {user.credits} Credits</span>
            )}

            {/* NOTIFICATION BELL — desktop only; hidden on mobile via CSS */}
            <div className="nav-bell-wrap nav-bell-desktop-only" ref={bellRef}>
              <button className={`nav-bell-btn${hasOutbid ? ' nav-bell-outbid' : ''}`} onClick={handleBellOpen} aria-label="Notifications">
                🔔
                {totalBadge > 0 && (
                  <span className="nav-bell-badge">{totalBadge > 9 ? '9+' : totalBadge}</span>
                )}
              </button>
              {bellOpen && (
                <div className="nav-bell-dropdown">
                  <div className="nbd-header">
                    <span className="nbd-title">Notifications</span>
                    {unreadCount > 0 && <span className="nbd-all-read">All marked read</span>}
                  </div>
                  {unreadMessages > 0 && (
                    <div className="nbd-msg-alert" onClick={handleMessageAlertClick}>
                      💬 {unreadMessages} unread message{unreadMessages !== 1 ? 's' : ''} — tap to view
                    </div>
                  )}
                  {notifications.length === 0 ? (
                    <p className="nbd-empty">No notifications yet.</p>
                  ) : (
                    notifications.slice(0, 10).map(n => (
                      <div
                        key={n.id}
                        className={`nbd-item ${n.read ? 'nbd-read' : 'nbd-unread'} nbd-clickable`}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <div className="nbd-msg">{n.message}</div>
                        <div className="nbd-time">{timeAgo(n.createdAt)}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <button className="btn btn-outline btn-sm" onClick={handleLogout}>Logout</button>
          </div>
        )}

        {/* Theme toggle inside menu — visible on mobile only */}
        <button className="nav-theme-in-menu" onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? '☀️ Light Mode' : '🌙 Dark Mode'}
        </button>
      </div>

      {/* Theme toggle outside menu — visible on desktop only */}
      <button className="nav-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>

      {/* NOTIFICATION BELL — mobile only, always visible in top bar */}
      {user && (
        <div className="nav-bell-wrap nav-bell-mobile-only" ref={mobileBellRef}>
          <button
            className={`nav-bell-btn${hasOutbid ? ' nav-bell-outbid' : ''}`}
            onClick={handleBellOpen}
            aria-label="Notifications"
          >
            🔔
            {totalBadge > 0 && (
              <span className="nav-bell-badge">{totalBadge > 9 ? '9+' : totalBadge}</span>
            )}
          </button>
          {bellOpen && (
            <div className="nav-bell-dropdown">
              <div className="nbd-header">
                <span className="nbd-title">Notifications</span>
                {unreadCount > 0 && <span className="nbd-all-read">All marked read</span>}
              </div>
              {unreadMessages > 0 && (
                <div className="nbd-msg-alert" onClick={handleMessageAlertClick}>
                  💬 {unreadMessages} unread message{unreadMessages !== 1 ? 's' : ''} — tap to view
                </div>
              )}
              {notifications.length === 0 ? (
                <p className="nbd-empty">No notifications yet.</p>
              ) : (
                notifications.slice(0, 10).map(n => (
                  <div
                    key={n.id}
                    className={`nbd-item ${n.read ? 'nbd-read' : 'nbd-unread'} nbd-clickable`}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className="nbd-msg">{n.message}</div>
                    <div className="nbd-time">{timeAgo(n.createdAt)}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <button className="nav-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
        <span /><span /><span />
      </button>
    </nav>
  );
};

export default NavigationBar;
